/**
 * Builds marketing FAQ / footnote strings from public display pricing (PBI #1193).
 * Callers must pass amounts from GET /api/public/subscription/config — never hard-code dollars.
 */

import { formatUsd } from './formatUsd';
import type { PublicSubscriptionConfig } from '../types/publicSubscriptionConfig';

/**
 * Formats a member discount percent for copy (drops trailing .00).
 */
export function formatMemberDiscountPercentLabel(percent: number): string {
  const rounded = Math.round(percent * 100) / 100;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded}%`;
}

/**
 * FAQ answer: free vs Personal plan.
 */
export function buildFaqFreePlanAnswer(config: PublicSubscriptionConfig): string {
  return (
    `The free plan lets you schedule static e-cards for up to ${config.freeContactLimit} contacts — ` +
    `no credit card required for static e-cards. Animated e-cards are ${formatUsd(config.animatedCardListPriceUsd)} each for free users, plus tax where applicable. ` +
    `Flower and mailed-card ordering are available on the free plan with a payment method on file (pay per order). ` +
    `The Personal subscription unlocks unlimited contacts, contact import from a file, Auto Send, ` +
    `AI writing assistant, and member pricing on mailed cards and animated e-cards.`
  );
}

/**
 * FAQ answer: who can order flowers, mailed cards, and other pay-per-order services.
 * Static copy — eligibility does not depend on display dollar amounts.
 */
export function buildFaqWhoCanOrderAnswer(): string {
  return (
    'No. Free accounts can order flowers and mailed cards pay-per-order with a payment method on file. ' +
    'Static e-cards are included on free; animated e-cards are charged per send at list price for free users. ' +
    'Personal is optional — it adds unlimited contacts, contact import from a file, Auto Send, AI writing help, and member pricing on ' +
    'mailed cards and animated e-cards. Flowers stay at the florist\u2019s catalog price with no markup from us on every plan ' +
    '(temporary sitewide florist sales may apply when shown).'
  );
}

/**
 * FAQ answer: mailed-card pay-per-order pricing.
 */
export function buildFaqMailedCardPricingAnswer(config: PublicSubscriptionConfig): string {
  const off = formatMemberDiscountPercentLabel(config.mailedCardMemberDiscountPercent);
  return (
    `No — each mailed card is a pay-per-order charge. List price is ${formatUsd(config.mailedCardListPriceUsd)} per card, plus tax where applicable; ` +
    `Personal members pay ${formatUsd(config.mailedCardMemberPriceUsd)} (${off} off). ` +
    `You need a payment method on file; mailed-card ordering is available on the free plan.`
  );
}

/**
 * FAQ answer: what member pricing is, with live list/member amounts.
 */
export function buildFaqMemberPricingAnswer(config: PublicSubscriptionConfig): string {
  const mailedOff = formatMemberDiscountPercentLabel(config.mailedCardMemberDiscountPercent);
  const animatedOff = formatMemberDiscountPercentLabel(config.animatedCardMemberDiscountPercent);

  return (
    `Member pricing is the lower pay-per-order rate Personal subscribers pay on mailed cards and animated e-cards. ` +
    `Mailed cards are ${formatUsd(config.mailedCardListPriceUsd)} list, plus tax where applicable, ` +
    `(${formatUsd(config.mailedCardMemberPriceUsd)} for members, ${mailedOff} off). ` +
    `Animated e-cards are ${formatUsd(config.animatedCardListPriceUsd)} list, plus tax where applicable, ` +
    `(${formatUsd(config.animatedCardMemberPriceUsd)} for members, ${animatedOff} off). ` +
    `Free accounts pay list. Flowers are charged at the florist\u2019s catalog price with no markup from Scheduled Smiles on every plan ` +
    `(temporary sitewide florist sales may apply when shown) — member pricing does not change flower cost.`
  );
}

/**
 * Pricing page / campaign footnote for flowers + mailed cards.
 */
export function buildPhysicalOrdersPricingFootnote(config: PublicSubscriptionConfig): string {
  return (
    `Flower orders are charged at the florist\u2019s catalog price with no markup from Scheduled Smiles ` +
    `(temporary sitewide florist sales may apply when shown). Mailed cards are ` +
    `${formatUsd(config.mailedCardListPriceUsd)} list, plus tax where applicable, (${formatUsd(config.mailedCardMemberPriceUsd)} for Personal members); ` +
    `optional gift cards add face value at checkout. Physical orders require a saved payment method.`
  );
}

/**
 * Compact Pricing page free-tier note.
 */
export function buildPricingPageMailedNote(config: PublicSubscriptionConfig): string {
  return (
    `Flower orders are charged at the florist\u2019s catalog price with no markup ` +
    `(temporary sitewide florist sales may apply when shown). Mailed cards are ${formatUsd(config.mailedCardListPriceUsd)} list, plus tax where applicable, ` +
    `(${formatUsd(config.mailedCardMemberPriceUsd)} for members). Card on file required on Free.`
  );
}

/**
 * Resolves campaign / marketing copy that may be a priced builder or a static string.
 * Uses the fallback when public config has not loaded yet.
 */
export function resolvePricedOrFallback(
  pricedOrStatic: string | ((config: PublicSubscriptionConfig) => string),
  fallback: string | undefined,
  config: PublicSubscriptionConfig | null,
): string {
  if (typeof pricedOrStatic === 'function') {
    return config ? pricedOrStatic(config) : (fallback ?? '');
  }

  return pricedOrStatic;
}
